// SHCT-607 — a controlled numeric <input> that actually shows what the user typed.
//
// THE BUG THIS EXISTS TO FIX. React's controlled-input sync for `type="number"`
// compares the DOM value against the new prop with LOOSE equality:
//
//     if (node.value != props.value) node.value = toString(props.value);   // ReactDOM
//
// With a numeric `value` prop that comparison is numeric, so `"05" != 5` is FALSE
// and React never rewrites the field. A field sitting at 0 that the user types "5"
// into therefore reads "05" forever: the state is correct (5), only the rendered
// text keeps the stray zero, and every further keystroke inherits it ("053" → 53).
//
// THE FIX. Hold the text the user is typing in a STRING draft and hand that string
// to the input. Two strings compare as strings, so `"05" != "5"` is true and React
// rewrites the DOM as soon as we normalize the draft. Leading zeros are stripped on
// every keystroke, so the field self-corrects instead of accumulating them.
//
// SCOPE. This fixes the leading zero only. It does NOT change how a half-typed
// decimal behaves: an input[type=number] runs the DOM value sanitization algorithm,
// so `e.target.value` is already "" while the text reads "1." — the browser never
// hands that string to anyone. Committing `emptyValue` at that moment is exactly what
// the call sites did before (`Number(e.target.value || 0)`), and React leaves the
// field's visible text alone because "" matches the sanitized DOM value, so the user
// can finish typing "1.5" normally. Keeping type="number" also preserves the native
// min/max/step guards several call sites rely on.

import { useEffect, useState, type InputHTMLAttributes } from 'react';

/**
 * Drop the zeros a number never needs: "05" → "5", "007" → "7".
 * Deliberately does NOT touch "0" (a legitimate value) or "0.5" (the zero is
 * significant there) — the pattern only fires when a DIGIT follows the zeros.
 */
export function stripLeadingZeros(raw: string): string {
  const match = /^(0+)(\d)/.exec(raw);
  return match ? raw.slice(match[1].length) : raw;
}

type NativeProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type'
>;

export interface NumberInputProps extends NativeProps {
  /** The committed numeric value. */
  value: number;
  /** Called with the parsed number on every edit that yields one. */
  onChange: (next: number) => void;
  /**
   * What an empty field commits. Call sites used to spell this as
   * `Number(e.target.value || 0)`; pass the same number here to keep a field's
   * existing clear-behaviour. Defaults to 0.
   */
  emptyValue?: number;
}

export function NumberInput({
  value,
  onChange,
  emptyValue = 0,
  onBlur,
  ...rest
}: NumberInputProps) {
  const [draft, setDraft] = useState(() => String(value));

  // Re-sync when the committed value moves for a reason other than this field —
  // a form reset, a detail fetch, an auto-filled Grade bound. The guard compares
  // NUMERICALLY on purpose: our own edits round-trip (draft "5" → value 5 → back
  // here) and must not clobber a draft the user is still mid-way through. It also
  // leaves a deliberately-cleared field empty, since Number('') === 0 === the
  // emptyValue it just committed.
  useEffect(() => {
    if (Number(draft) !== value) {
      setDraft(String(value));
    }
    // `draft` is read but intentionally not a dependency: this effect exists to
    // react to OUTSIDE value changes, and re-running it on every keystroke would
    // undo the draft it is meant to protect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <input
      {...rest}
      type="number"
      value={draft}
      onChange={(e) => {
        const next = stripLeadingZeros(e.target.value);
        setDraft(next);
        if (next.trim() === '') {
          onChange(emptyValue);
          return;
        }
        const parsed = Number(next);
        // A half-typed decimal ("1.", "0.") parses fine, so it commits as it goes.
        // Anything genuinely unparseable (the browser lets "e"/"-" through on
        // type=number) stays in the draft without committing a NaN.
        if (Number.isFinite(parsed)) {
          onChange(parsed);
        }
      }}
      onBlur={(e) => {
        // Settle the field: an empty or half-typed draft ("1.") renders as the
        // committed value once focus leaves.
        setDraft(String(value));
        onBlur?.(e);
      }}
    />
  );
}
